// ticket-acceptance-evidence-check.ts
//
// C1 layer B (plan 20260915-1650 phase-03): a ticket's `acceptance[]` row
// ticked `done: true` must point at something. Same shape the project has
// already paid for twice — audit 2026-09-14 finding 2 (`evidence: []` walked
// past the verifier because nothing asked), and P20's review-local attempt
// that reached `done` having written no report at all.
//
// Its own file rather than another function in result-verifier-checks.ts:
// when this was written that file was at 202 lines and result-verifier.ts at
// 275, both over the 200-line project budget before that phase touched them.
// Adding here kept the overshoot from growing. (result-verifier.ts was later
// split below the budget — plan 20260924-0205.)
//
// Reads the TICKET, never the result. `acceptance` lives on the git-versioned
// ticket the controller owns; a worker changes it only through
// proposed_changes, which supervisor-proposal-application.ts validates. If
// this check read a worker-declared array instead, an attempt could discharge
// it by shipping `acceptance: []` — the `kind`-vs-`command_adapter` lesson
// (Bug K) applied to a different field.
import type { TicketAcceptance } from "./schemas/ticket-schema.js";

/**
 * Returns a reason string when some row claims `done` with no evidence
 * behind it, `undefined` when the ticket is clean or says nothing.
 *
 * Absence (`undefined`) is NOT a violation: that is every ticket authored
 * before C1, the two `mission-initializer-tickets.ts` seeds, and the six e2e
 * fixtures. An empty ARRAY is likewise clean — phase-01's distinction between
 * "declared nothing to list" and "never answered" holds here too.
 */
export function checkAcceptanceDoneHasEvidence(
  acceptance: readonly TicketAcceptance[] | undefined,
): string | undefined {
  if (acceptance === undefined) return undefined;

  const unproven = acceptance.filter(
    // `.trim()` on purpose: the schema's `.min(1)` accepts "   ", and a
    // whitespace string is not a place anyone can go look.
    (row) => row.done && (row.evidence === undefined || row.evidence.trim() === ""),
  );
  if (unproven.length === 0) return undefined;

  const quoted = unproven.map((row) => `"${row.text}"`).join(", ");
  return (
    `acceptance có ${unproven.length} điều kiện khai done nhưng không có evidence: ${quoted} — ` +
    "tick done mà không trỏ được vào bằng chứng là khai vượt cái quan sát được " +
    "(§5.4: verifier là nơi DUY NHẤT quyết định done)."
  );
}
